// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for editing asset serial numbers.
 * This module provides a focused interface for updating
 * an asset's serial number with validation and uniqueness
 * checks. It ensures serial numbers remain unique across
 * all assets in the system.
 *
 * Key features:
 * - Serial validation
 * - Uniqueness checks
 * - Error handling
 * - Real-time updates
 * - Form validation
 */
import { handleAPIError, hasUniqueViolationError } from '@/api/error-utils';
import { ExistingAsset } from '@/api/types';
import { Asset } from '@/asset/api/types';
import { UpdateAssetParameters, updateAsset } from '@/asset/api/update-asset';
import { TakenSerialLink } from '@/asset/components/taken-serial-link';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating serial numbers.
 * Ensures the serial is not empty and meets
 * minimum length requirements.
 */
const serialSchema = z.string().min(1, 'Serial is required');

/**
 * Schema for validating the complete form data.
 * Currently only contains the serial field but
 * could be extended for additional fields.
 */
const formSchema = z.object({ serial: serialSchema });

/**
 * Type definition for form data.
 * Used for both input and output validation.
 *
 * @typedef {Object} FormData
 */
type FormData = z.infer<typeof formSchema>;

/**
 * Props for configuring the AssetSerialDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to edit.
   * Only requires the key and serial fields.
   */
  asset: Pick<Asset, 'key' | 'serial'>;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for editing asset serial numbers.
 * Provides a simple form interface for updating an asset's
 * serial number with validation and uniqueness checks.
 *
 * Features:
 * - Serial number validation
 * - Uniqueness enforcement
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset }: { asset: Asset }) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <AssetSerialDialog
 *       asset={asset}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function AssetSerialDialog(props: Props) {
  const queryClient = useQueryClient();
  const [takenSerialAsset, setTakenSerialAsset] =
    useState<ExistingAsset | null>(null);

  const updateAssetMutation = useMutation({
    mutationFn: (parameters: UpdateAssetParameters) =>
      updateAsset(props.asset.key, parameters),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset', props.asset.key] });
      reset({ serial: data.asset.serial });
      close();
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        setTakenSerialAsset(handleAPIError(error).existingAsset ?? null);
        setError('serial', {
          type: 'custom',
          message: 'An asset with this serial already exists',
        });
      }
    },
  });

  const { control, formState, reset, setError, handleSubmit } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: { serial: props.asset.serial },
    });

  useEffect(() => {
    if (props.isOpen) {
      reset({ serial: props.asset.serial });
    }
  }, [props.isOpen, props.asset.serial, reset]);

  const shouldUpdate = formState.isDirty;
  // Read every render, so react-hook-form subscribes to the serial error.
  const hasSerialError = formState.errors.serial !== undefined;
  const isAlertOpen =
    updateAssetMutation.isError &&
    !hasUniqueViolationError(updateAssetMutation.error);

  /**
   * Closes the dialog and resets the form state.
   * Restores the original serial number.
   */
  const close = () => {
    props.onClose();
    reset();
    setTakenSerialAsset(null);
  };

  /**
   * Handles form submission when validation passes.
   * Updates the asset's serial number if changed.
   *
   * @param data - The validated form data
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldUpdate) {
      setTakenSerialAsset(null);
      return updateAssetMutation.mutate({ serial: data.serial });
    }

    close();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={isAlertOpen}
        onClose={updateAssetMutation.reset}
        message="An error occurred while updating the asset."
      />
      <DialogTitle>Edit asset</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5}>
          <FormTextField
            fullWidth
            name="serial"
            label="Serial"
            control={control}
          />
          {takenSerialAsset !== null && hasSerialError && (
            <TakenSerialLink existingAsset={takenSerialAsset} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
